import React from 'react'
import { ScheduleCard, type ScheduleCardColor } from '@enamelai/design-system'
import { Icon as ArrowCircleTopRightIcon } from '@enamelai/design-system/icons/Arrow_circle_top_right'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark2'
import { Icon as ClaimsIcon } from '@enamelai/design-system/icons/Claims'
import { Icon as HealthIcon } from '@enamelai/design-system/icons/Health'
import { Icon as InTreatmentIcon } from '@enamelai/design-system/icons/In_treatment'
import { Icon as MoreIcon } from '@enamelai/design-system/icons/More1'
import { Icon as RecurringIcon } from '@enamelai/design-system/icons/Recurring'
import type { SchedulerSettingsType } from './Scheduler.component'

type PreviewCard = {
  id: string
  column: number
  top: number
  height: number
  color: ScheduleCardColor
  timeRange: string
  appointmentType: string
  MetaIcon: React.FC<React.SVGProps<SVGSVGElement>>
  StatusIcon: React.FC<React.SVGProps<SVGSVGElement>>
}

const PREVIEW_COLUMNS = 5

const PREVIEW_CARDS: PreviewCard[] = [
  {
    id: 'general',
    column: 2,
    top: 12,
    height: 112,
    color: 'blue',
    timeRange: '9:15 AM - 11:45 AM',
    appointmentType: 'General',
    MetaIcon: RecurringIcon,
    StatusIcon: InTreatmentIcon,
  },
  {
    id: 'follow-up',
    column: 3,
    top: 54,
    height: 56,
    color: 'teal',
    timeRange: '10:00 AM - 11:00 AM',
    appointmentType: 'General',
    MetaIcon: ArrowCircleTopRightIcon,
    StatusIcon: CheckmarkIcon,
  },
  {
    id: 'periodontal',
    column: 1,
    top: 110,
    height: 82,
    color: 'purple',
    timeRange: '11:00 AM - 12:30 PM',
    appointmentType: 'Periodontal (Prosthesis removal)',
    MetaIcon: HealthIcon,
    StatusIcon: CheckmarkIcon,
  },
]

/** Pixel height of one time slot row per minute of slot interval. */
const SLOT_PX_PER_MINUTE = 1.8

type SchedulerPreviewProps = {
  settings: SchedulerSettingsType
}

const SchedulerPreview: React.FC<SchedulerPreviewProps> = ({ settings }) => {
  const slotHeight =
    (Number(settings.time_slot_interval) || 15) * SLOT_PX_PER_MINUTE

  return (
    <div
      className="scheduler-preview"
      aria-hidden="true"
      style={
        { '--scheduler-preview-slot': `${slotHeight}px` } as React.CSSProperties
      }
    >
      <div className="scheduler-preview__columns">
        {Array.from({ length: PREVIEW_COLUMNS }).map((_, index) => (
          <span key={index} className="scheduler-preview__column" />
        ))}
      </div>
      {PREVIEW_CARDS.map(
        ({ id, column, top, height, MetaIcon, StatusIcon, ...card }) => (
          <div
            key={id}
            className="scheduler-preview__card"
            style={
              {
                '--scheduler-preview-column': column,
                top,
                height,
              } as React.CSSProperties
            }
          >
            <ScheduleCard
              color={card.color}
              timeRange={card.timeRange}
              patientName="Patient name"
              appointmentType={card.appointmentType}
              showTime={settings.show_appointment_duration}
              showAppointmentType={settings.show_appointment_type}
              headerIcons={
                <>
                  <MetaIcon />
                  {settings.status_indicator ? <StatusIcon /> : null}
                  <ClaimsIcon />
                </>
              }
            />
            <MoreIcon className="scheduler-preview__more" />
          </div>
        ),
      )}
    </div>
  )
}

export default SchedulerPreview
